import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  CssBaseline,
  useTheme,
  useMediaQuery,
  Collapse,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { createProject, completeOnboarding } from '../services/api';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';

const Onboarding = () => {
  const [loading, setLoading] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [projectData, setProjectData] = useState({
    name: '',
    description: '',
  });
  const [communicationPrefs, setCommunicationPrefs] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { reloadProjects, setSelectedProject } = useProject();
  const { updateUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Track onboarding start event
    window.gtag('event', 'onboarding_started', {
      'event_category': 'engagement',
      'event_label': 'onboarding',
      'value': 1,
      'device_type': isMobile ? 'mobile' : 'desktop'
    });
  }, [isMobile]);

  const handleProjectDataChange = (field) => (event) => {
    setProjectData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCreateProject = async () => {
    try {
      setLoading(true);
      
      // Create project
      const newProject = await createProject(projectData);
      await reloadProjects();
      
      // Complete onboarding with communication preferences
      const updatedUser = await completeOnboarding({
        communication_preferences: {
          product_updates: communicationPrefs
        }
      });
      updateUser(updatedUser);
      
      // Track lead generation event
      window.gtag('event', 'generate_lead', {
        'event_category': 'conversion',
        'event_label': 'generate_lead',
        'value': 1,
      });
      
      // Select the newly created project
      setSelectedProject(newProject);
      
      enqueueSnackbar('Project created successfully!', { variant: 'success' });
      navigate('/dashboard');
    } catch (error) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to create project', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const isValid = () => {
    return projectData.name.trim() !== '';
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.default,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <CssBaseline />
      
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          opacity: 0.05,
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
          opacity: 0.05,
          zIndex: 0
        }}
      />

      <Container 
        maxWidth="sm" 
        sx={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
          py: isMobile ? 3 : 4,
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: isMobile ? 3 : 4,
            width: '100%',
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(66, 66, 66, 0.8)' 
              : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
          }}
        >
          <Typography 
            variant={isMobile ? "h5" : "h4"}
            align="center" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 1
            }}
          >
            Welcome to Writellect
          </Typography>
          
          <Typography 
            variant={isMobile ? "body1" : "h6"}
            align="center" 
            color="text.secondary" 
            sx={{ mb: 4 }}
          >
            Let's set up your workspace and get you started
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Create Your First Project
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A project will be used to organize your content and settings.
            </Typography>

            <TextField
              fullWidth
              label="Project Name"
              value={projectData.name}
              onChange={handleProjectDataChange('name')}
              margin="normal"
              required
              error={projectData.name.trim() === ''}
              helperText={projectData.name.trim() === '' ? 'Project name is required' : ''}
              sx={{ mb: 3 }}
            />

            <Button
              onClick={() => setShowDescription(!showDescription)}
              endIcon={showDescription ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              sx={{ 
                mb: 1,
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'rgba(0, 0, 0, 0.05)',
                }
              }}
            >
              Add description (Optional)
            </Button>

            <Collapse in={showDescription}>
              <TextField
                fullWidth
                value={projectData.description}
                onChange={handleProjectDataChange('description')}
                margin="normal"
                multiline
                rows={2}
                placeholder="What kind of content do you want to create?"
                sx={{ mb: 3 }}
              />
            </Collapse>
          </Box>

          <Box 
            sx={{ 
              mb: 4,
              p: 2,
              borderRadius: 1,
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(0, 0, 0, 0.02)',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={communicationPrefs}
                  onChange={(e) => setCommunicationPrefs(e.target.checked)}
                  color="primary"
                  sx={{ 
                    p: 1,
                    '& .MuiSvgIcon-root': {
                      fontSize: 28,
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" color="text.primary" sx={{ mb: 0.5 }}>
                    Stay in the Loop
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Get notified about new features and improvements to help you create better content
                  </Typography>
                </Box>
              }
              sx={{ 
                m: 0,
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': {
                  flex: 1,
                  mt: 0.5
                }
              }}
            />
          </Box>

          <Button
            variant="contained"
            onClick={handleCreateProject}
            disabled={!isValid() || loading}
            fullWidth
            size="large"
            sx={{ 
              py: 2,
              position: 'relative',
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '1.1rem'
            }}
          >
            {loading ? (
              <CircularProgress 
                size={24} 
                sx={{ 
                  color: 'inherit',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px'
                }}
              />
            ) : (
              'Complete Setup'
            )}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default Onboarding; 